import React, { createContext, type ReactNode, useEffect } from 'react';
import { token } from '@atlaskit/tokens';

export const EmbedModeContext: React.Context<boolean> = createContext<boolean>(false);
const { Provider } = EmbedModeContext;

type EmbedSurface = 'elevation.surface' | 'elevation.surface.sunken' | 'elevation.surface.overlay';

type Props = {
	children: ReactNode;
	/**
	 * The elevation the host paints behind the iframe, which the embed has to sit at to avoid a
	 * visible seam. See the token guidance at
	 * https://hello.atlassian.net/wiki/spaces/~460573267/pages/2355983526/Jira+Dark+Mode+-+UI+Guidelines#Element-elevation-basics
	 *
	 * - `elevation.surface` when the host uses the primary UI surface.
	 * - `elevation.surface.sunken` when the host groups the embed with sibling content, the way Jira
	 *   cards sit in columns.
	 * - `elevation.surface.overlay` when the host floats the embed above its UI, as a modal, dialog,
	 *   or dropdown does.
	 */
	surface?: EmbedSurface;
};

const handleEmbedLinkClick = (e: Event) => {
	if (e.target instanceof Element) {
		const anchor = e.target.closest('a'); // Find closest parent anchor (including the target itself)
		if (anchor && anchor.target !== '_blank') {
			// link
			anchor.target = '_blank';
		}
	}
};

const BACKGROUND_SURFACES = {
	'elevation.surface': token('elevation.surface'),
	'elevation.surface.sunken': token('elevation.surface.sunken'),
	'elevation.surface.overlay': token('elevation.surface.overlay'),
} as const;

// Assumes the embed is the whole document of an iframe rendered by a host, mounted inside the Jira
// SPA shell — so the surrounding elevation is the host's, not Jira's. The global container paints
// `#jira-frontend` with `elevation.surface` before this Provider mounts, so any host at a different
// elevation needs that overrider overridden here rather than in the embed's own layout.
const MonolithSurfaceOverride = ({ surface }: { surface: EmbedSurface }) => (
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
	<style
		// oxlint-disable-next-line react/no-danger
		dangerouslySetInnerHTML={{
			__html: `
                    #jira-frontend {
                        background: ${BACKGROUND_SURFACES[surface]};
                    }
                `,
		}}
	/>
);

export const EmbedModeProvider = ({ children, surface }: Props): React.JSX.Element => {
	useEffect(() => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.addEventListener('click', handleEmbedLinkClick, true);
		return () => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.removeEventListener('click', handleEmbedLinkClick);
		};
	}, []);

	return (
		<>
			{surface && <MonolithSurfaceOverride surface={surface} />}
			<Provider value>{children}</Provider>
		</>
	);
};
